// Public entry of @sirius/intake-callback.
//
// Usage on a website (per-tenant host — apiBase matches the site's own api.<domain>):
//   <script src="https://api.sirius.if.ua/widget/intake-callback.v1.js"></script>
//   <script>
//     const ic = SiriusIntakeCallback.init({
//       siteKey: 'sirius.if.ua',
//       apiBase: 'https://api.sirius.if.ua',
//     })
//     const { token, willCallNow } = await ic.submitCallback({ phone: '+380…' })
//     ic.subscribeStatus(token, (s) => updateUI(s))
//   </script>
//
// Honeypot: the site renders a hidden input named 'hp'. Bots fill it, humans
// don't. The SDK forwards whatever value is in the form field — the server
// silently drops submissions with non-empty hp. The SDK never adds hp itself.

import { createClient, IntakeApiError } from './client'
import { subscribeStatus } from './status-poller'
import type {
  CallbackStatus,
  InitConfig,
  SiriusIntakeCallback,
  SubmitCallbackInput,
  SubmitCallbackResult,
  WorkingHours,
} from './types'

export type {
  CallbackState,
  CallbackStatus,
  InitConfig,
  Locale,
  SubmitCallbackInput,
  SubmitCallbackResult,
  SiriusIntakeCallback,
  WorkingHours,
} from './types'

export { IntakeApiError } from './client'

/** Initialise the SDK for a single site. Returns the public API surface. */
export function init(config: InitConfig): SiriusIntakeCallback {
  if (!config.siteKey) throw new Error('@sirius/intake-callback: siteKey is required')
  if (!config.apiBase) throw new Error('@sirius/intake-callback: apiBase is required')

  const client = createClient({
    apiBase: config.apiBase,
    locale: config.locale,
    fetchImpl: config.fetchImpl,
  })

  return {
    async submitCallback(input: SubmitCallbackInput): Promise<SubmitCallbackResult> {
      const payload: Record<string, unknown> = {
        site_key: config.siteKey,
        form_type: 'callback',
        phone: input.phone,
        name: input.name ?? '',
        message: '',
        payload: input.page ? { page: input.page } : {},
      }
      if (input.hp !== undefined && input.hp !== null) {
        // Honeypot forwarded from the site-rendered hidden field. Server
        // silently drops if non-empty.
        payload.hp = input.hp
      }
      const resp = await client.post<{ id: number; status: string; token?: string; will_call_now?: boolean }>(
        '/api/v2/ecommerce/storefront/public/submit',
        payload,
      )
      // token + will_call_now are populated by intake once TS-127 Phase 3 lands.
      // Before that, callers degrade gracefully: id is the intake submission
      // numeric id (not yet a UUID), will_call_now is unknown.
      const token = resp.token ?? String(resp.id)
      const willCallNow = resp.will_call_now ?? false
      return { token, willCallNow }
    },

    async getStatus(token: string): Promise<CallbackStatus> {
      return await client.get<CallbackStatus>(`/api/v2/ecommerce/storefront/public/callback/${encodeURIComponent(token)}`)
    },

    subscribeStatus(token: string, cb: (s: CallbackStatus) => void): () => void {
      return subscribeStatus(token, (t) => this.getStatus(t), cb)
    },

    async getWorkingHours(): Promise<WorkingHours> {
      return await client.get<WorkingHours>(`/api/v2/ecommerce/storefront/public/sites/${encodeURIComponent(config.siteKey)}/hours`)
    },

    normalizeUAPhone(raw: string): string | null {
      return normalizeUAPhone(raw)
    },
  }
}

/**
 * normalizeUAPhone accepts what people actually type and returns the canonical
 * 0XXXXXXXXX form. Mirrors the backend validator so the form can validate
 * inline. Returns null when the input isn't a valid UA mobile.
 *
 * Accepted: '0501234567', '+380 50 123 45 67', '380-50-123-45-67', spaces/dashes.
 */
export function normalizeUAPhone(raw: string): string | null {
  if (!raw) return null
  let digits = ''
  for (const ch of raw) {
    if (ch >= '0' && ch <= '9') digits += ch
  }
  if (digits.startsWith('380')) {
    // strip country code → leaves 9 subscriber digits, prepend trunk 0
    digits = '0' + digits.slice(3)
  }
  // After normalisation a valid UA number is exactly 10 digits starting with 0.
  return /^0\d{9}$/.test(digits) ? digits : null
}
